import { FC } from "react";
import { MenuProps } from "../../types";
import { Theme } from "../../../types";
import {
  megaMenuItemContainer,
  megaMenuItemActive,
  megaMenuItemTitle,
  megaMenuItemTitlePrimary,
  megaMenuItemDesc,
  megaMenuIconWrapper,
  activeRouteStyles,
  stylesText
} from "../../styles";
import ArrowFill from "@/nav-localized/assets/header/nav/arrow-fill.svg?react";
import MenuBadge from "@/nav-localized/components/MenuBadge";
import clsx from "clsx";
import { useTranslations } from "use-intl";

interface MegaMenuItemProps {
  item: MenuProps;
  theme: Theme;
  localeLink: string;
  locale: string;
  onNavigate: (item: MenuProps, e: React.MouseEvent<HTMLAnchorElement>) => void;
  /** 是否处于激活高亮状态（hover isPrimary 项时由 Panel 控制） */
  isActive?: boolean;
  /** hover 此条目时通知 Panel（用于三级展开控制） */
  onHover?: () => void;
}

/**
 * 菜单条目组件
 *
 * 支持两种视觉状态：
 * - 普通状态：默认样式 + hover 时微背景
 * - 激活状态（isActive）：高亮背景 + 品牌色标题
 *
 * 箭头规则（匹配 Figma 设计稿）：
 * - isPrimary 项：始终显示右侧箭头（24×24），固定可见
 * - 非 isPrimary 项：不渲染箭头
 */
const MegaMenuItem: FC<MegaMenuItemProps> = ({
  item,
  theme,
  localeLink,
  onNavigate,
  isActive = false,
  onHover
}) => {
  const t = useTranslations("header");
  const showArrow = !!item.isPrimary;
  // 改版后 isPrimary 主要用于 hover 展开右侧内容，但“带 URL 的条目”仍应保持改版前的跳转能力
  const canNavigate =
    !!item.clickFunc ||
    !!item.outbound ||
    // 允许 url 为空字符串：部分跨站点路由使用 basePath + "" 表示根路径
    (typeof item.url === "string" && (item.url.length > 0 || !!item.basePath));
  const disableNav = !canNavigate;

  const buildHref = () => {
    if (disableNav) return undefined;
    if (item.outbound) return item.url ?? "#";
    if (item.basePath) return `/${item.basePath}${localeLink}/${item.url ?? ""}`;
    return `${localeLink}/${item.url}`;
  };

  return (
    <a
      href={buildHref()}
      onClick={(e) => {
        if (disableNav) {
          e.preventDefault();
          return;
        }
        onNavigate(item, e);
      }}
      onMouseEnter={onHover}
    >
      <div
        className={clsx(
          isActive ? megaMenuItemActive({ mode: theme }) : megaMenuItemContainer({ mode: theme }),
          showArrow && "!gap-[4px]",
          "group/item"
        )}
      >
        {showArrow ? (
          <>
            {/* isPrimary 项：内容包裹在 flex-1 容器中，箭头在外层 */}
            <div className="flex flex-1 gap-[12px] items-center min-w-0">
              {item.icon && (
                <item.icon
                  className={clsx("w-[24px] h-[24px]", megaMenuIconWrapper({ mode: theme }))}
                />
              )}
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex items-center">
                  <span
                    className={clsx(
                      "whitespace-nowrap",
                      isActive ? megaMenuItemTitlePrimary() : megaMenuItemTitle({ mode: theme })
                    )}
                  >
                    {t(item.label)}
                  </span>
                  {(item.extraBadges ?? []).map((badge, i) => (
                    <MenuBadge key={i} text={badge} className="ml-[6px]" />
                  ))}
                </div>
                {item.des && (
                  <span className={clsx(megaMenuItemDesc({ mode: theme }), "mt-[2px]")}>
                    {t(item.des)}
                  </span>
                )}
              </div>
            </div>
            <ArrowFill
              className={clsx(
                "w-[24px] h-[24px] shrink-0 rtl-mirror",
                isActive ? activeRouteStyles({ mode: theme }) : stylesText({ mode: theme })
              )}
            />
          </>
        ) : (
          <>
            {/* 非 isPrimary 项：无箭头 */}
            {item.icon && (
              <item.icon
                className={clsx("w-[24px] h-[24px]", megaMenuIconWrapper({ mode: theme }))}
              />
            )}
            <div className="flex flex-col flex-1 min-w-0">
              <div className="flex items-center">
                <span className={clsx("whitespace-nowrap", megaMenuItemTitle({ mode: theme }))}>
                  {t(item.label)}
                </span>
                {(item.extraBadges ?? []).map((badge, i) => (
                  <MenuBadge key={i} text={badge} className="ml-[6px]" />
                ))}
              </div>
              {item.des && (
                <span className={clsx(megaMenuItemDesc({ mode: theme }), "mt-[2px]")}>
                  {t(item.des)}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </a>
  );
};

export default MegaMenuItem;
